/**
 * EditableNumberSlider — slider + direct numeric input pair.
 *
 * Same visual language as the page-local NumberSlider components, plus a
 * compact <input type="number"> so exact values can be typed straight in
 * (including values beyond the slider max — the slider just pins at max
 * while the typed value stands). Commits on blur/Enter, clamps to >= 0,
 * and drives the same onChange/patch path as the slider.
 */
import { useEffect, useState } from 'react'

export default function EditableNumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
  help,
  caption,
  className,
  prefix = '£',
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
  /** Optional tooltip help text (shown as title + "?" affordance). */
  help?: string
  /** Optional small caption rendered beside the numeric input. */
  caption?: string
  /** Optional wrapper classes (e.g. ScenarioStacker lever styling). */
  className?: string
  /** Symbol shown before the numeric input. Pass '' to hide. */
  prefix?: string
}) {
  const safeValue = Number.isFinite(value) ? (value ?? 0) : 0
  const [text, setText] = useState<string>(String(Math.round(safeValue)))

  // Re-sync the text box whenever the committed value changes elsewhere
  // (slider drag, hydration from the shared planning store, etc.).
  useEffect(() => {
    setText(String(Math.round(Number.isFinite(value) ? (value ?? 0) : 0)))
  }, [value])

  const commit = () => {
    const parsed = parseFloat(text)
    const next = Number.isFinite(parsed) ? Math.max(0, parsed) : safeValue
    setText(String(Math.round(next)))
    if (next !== safeValue) onChange(next)
  }

  return (
    <div className={className} title={help}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <label className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
          {label}
          {help && (
            <span
              className="text-[9px] text-text-muted/60 cursor-help"
              aria-label={`Help: ${help}`}
            >
              ?
            </span>
          )}
        </label>
        <span className="text-xs font-bold text-text-primary">{format(safeValue)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(Math.max(safeValue, min), max)}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
        title={help}
      />
      <div className="flex items-center gap-1.5 mt-1">
        {prefix && <span className="text-[10px] text-text-muted">{prefix}</span>}
        <input
          type="number"
          inputMode="numeric"
          min={0}
          step={step}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          className="w-24 max-w-full bg-surface-card border border-border rounded px-2 py-1 text-xs text-text-primary font-mono"
          aria-label={`${label} — type exact value`}
        />
        {caption && <span className="text-[10px] text-text-muted whitespace-nowrap">{caption}</span>}
      </div>
    </div>
  )
}
